import React, { useState } from 'react';
import { ExploreConfig } from '@explore-with-ai/core';
import HomePage from './components/HomePage';
import DemoPage from './components/DemoPage';
import DocumentationPage from './components/DocumentationPage';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import ConfigForm from './components/ConfigForm';
import WidgetPreview from './components/WidgetPreview';
import CodeGenerator from './components/CodeGenerator';
import AIHandoffPage from './components/AIHandoffPage';
import './App.css';

const App: React.FC = () => {
  const [config, setConfig] = useState<ExploreConfig>({
    id: '',
    title: '',
    description: '',
    sourceName: '',
    sourceUrl: '',
    format: 'Interactive Visualization',
    lastUpdated: 'Updated regularly',
    fields: [],
    modes: ['general', 'trends', 'anomalies', 'policy'],
    enabledPlatforms: ['chatgpt', 'claude', 'gemini', 'perplexity', 'grok'],
    buttonStyle: 'default',
    audience: 'general'
  });

  const [currentView, setCurrentView] = useState<'home' | 'demo' | 'docs' | 'about' | 'privacy' | 'config' | 'preview' | 'code' | 'handoff'>('home');
  const [handoffMode, setHandoffMode] = useState<string>('general');

  const handleConfigChange = (newConfig: ExploreConfig) => {
    setConfig(newConfig);
  };

  const isConfigValid = config.title && config.description && config.sourceName && config.sourceUrl;

  return (
    <div className="app">
      {currentView === 'home' && (
        <HomePage 
          onGetStarted={() => setCurrentView('config')}
          onViewDemo={() => setCurrentView('demo')}
          onNavigate={(page: string) => setCurrentView(page as any)}
        />
      )}
      
      {currentView === 'demo' && (
        <DemoPage 
          onBackToHome={() => setCurrentView('home')}
          onGetStarted={() => setCurrentView('config')}
        />
      )}
      
      {currentView === 'docs' && (
        <DocumentationPage 
          onBackToHome={() => setCurrentView('home')}
          onGetStarted={() => setCurrentView('config')}
        />
      )}
      
      {currentView === 'about' && (
        <AboutPage 
          onBackToHome={() => setCurrentView('home')}
          onGetStarted={() => setCurrentView('config')}
        />
      )}
      
      {currentView === 'privacy' && (
        <PrivacyPage 
          onBackToHome={() => setCurrentView('home')}
        />
      )}
      
      {currentView !== 'home' && currentView !== 'demo' && currentView !== 'docs' && currentView !== 'about' && currentView !== 'privacy' && (
        <div className="app__builder">
          <div className="app__header">
            <button 
              className="app__back-button"
              onClick={() => setCurrentView('home')}
            >
              ← Back to Home
            </button>
            <h1 className="app__title">Explore with AI Builder</h1>
            
            <nav className="app__nav">
              <button 
                className={`app__nav-item ${currentView === 'config' ? 'active' : ''}`}
                onClick={() => setCurrentView('config')}
              >
                1. Configure
              </button>
              <button 
                className={`app__nav-item ${currentView === 'preview' ? 'active' : ''}`}
                onClick={() => setCurrentView('preview')}
                disabled={!isConfigValid}
              >
                2. Preview
              </button>
              <button 
                className={`app__nav-item ${currentView === 'code' ? 'active' : ''}`}
                onClick={() => setCurrentView('code')}
                disabled={!isConfigValid}
              >
                3. Export
              </button>
            </nav>
          </div>

          <div className="app__main">
            {currentView === 'config' && (
              <ConfigForm 
                config={config} 
                onChange={handleConfigChange}
                onNext={() => setCurrentView('preview')}
              />
            )}
            
            {currentView === 'preview' && (
              <WidgetPreview 
                config={config}
                onBack={() => setCurrentView('config')}
                onNext={() => setCurrentView('code')}
                onHandoff={(mode) => {
                  setHandoffMode(mode);
                  setCurrentView('handoff');
                }}
              />
            )}
            
            {currentView === 'code' && (
              <CodeGenerator 
                config={config}
                onBack={() => setCurrentView('preview')}
              />
            )}
            
            {currentView === 'handoff' && (
              <AIHandoffPage
                config={config}
                mode={handoffMode}
                audience={config.audience}
                onBack={() => setCurrentView('preview')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
